import apiService from './api';
import type {
  Admin,
  AdminCurrentScopesResponse,
  ChurchAdminsListFiltersData,
  PagedListRequest,
  PagedListResponse,
} from '@/types';
import { formatQueryString } from '@/helpers';
import { DEFAULT_LIST_LIMIT_FILTER } from '@/constants';

class ChurchAdminsService {
  getCurrentScopes = async (filters?: PagedListRequest): Promise<AdminCurrentScopesResponse> => {
    try {
      const response = await apiService.get<AdminCurrentScopesResponse>(
        `/church-admins/current/scopes${formatQueryString({
          limit: DEFAULT_LIST_LIMIT_FILTER,
          page: 1,
          ...filters,
        })}`
      );
      return response.data;
    } catch (e) {
      console.error('Error fetching current scopes:', e);
      throw e;
    }
  };

  getChurchAdminsList = async (churchId: string, filters?: ChurchAdminsListFiltersData) => {
    try {
      const response = await apiService.get<PagedListResponse<Admin>>(`/admin/church/${churchId}/church-admins`, {
        params: filters,
      });
      return response;
    } catch (e) {
      console.error('Error fetching church admins list:', e);
      throw e;
    }
  };

  updateChurchAdmins = async (churchId: string, memberIds: string[]) => {
    try {
      const response = await apiService.put<Admin[]>(`/admin/church/${churchId}/church-admins`, memberIds);
      return response.data;
    } catch (e) {
      console.error('Error updating church admins:', e);
      throw e;
    }
  };

  createChurchAdmin = async (churchId: string, data: Admin) => {
    try {
      const response = await apiService.post<Admin>(`/admin/church/${churchId}/church-admins`, data);
      return response.data;
    } catch (e) {
      console.error('Error creating church admin:', e);
      throw e;
    }
  };

  getChurchAdmin = async (churchId: string, adminId: string) => {
    try {
      const response = await apiService.get<Admin>(`/admin/church/${churchId}/church-admins/${adminId}`);
      return response.data;
    } catch (e) {
      console.error('Error fetching church admin:', e);
      throw e;
    }
  };

  updateChurchAdmin = async (churchId: string, adminId: string, data: Admin) => {
    try {
      const response = await apiService.put<Admin>(`/admin/church/${churchId}/church-admins/${adminId}`, data);
      return response.data;
    } catch (e) {
      console.error('Error updating church admin:', e);
      throw e;
    }
  };

  deleteChurchAdmin = async (churchId: string, adminId: string) => {
    try {
      await apiService.delete(`/admin/church/${churchId}/church-admins/${adminId}`);
    } catch (e) {
      console.error('Error deleting church admin:', e);
      throw e;
    }
  };

  sendChurchAdminLink = async (churchId: string, adminId: string) => {
    try {
      await apiService.post(`/admin/church/${churchId}/church-admins/${adminId}/link/send`);
    } catch (e) {
      console.error('Error sending church admin link:', e);
      throw e;
    }
  };

  searchChurchAdminCandidates = async (churchId: string, filters?: ChurchAdminsListFiltersData) => {
    try {
      const response = await apiService.get<PagedListResponse<Admin>>(
        `/admin/church/${churchId}/church-admins/candidates/search`,
        { params: filters }
      );
      return response;
    } catch (e) {
      console.error('Error searching church admin candidates:', e);
      throw e;
    }
  };
}

export default new ChurchAdminsService();
